import {
    HardwareHealthNotificationSubscriptionResponse,
    HardwareHealthNotificationSubscriptionUpdateRequest,
    HardwareHealthNotificationTriggerRequestChild,
    IHardwareHealthNotificationTriggerRequestChild,
} from '@kcftech/prometheus-client';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';

/**
 * Configuration object for editing hardware health notification subscriptions.
 */
export type EditHardwareHealthNotificationSubscription = Readonly<{
    /**
     * Id of the notification subscription to edit.
     */
    id: string;
    /**
     * Whether or not this notification should be silenced. Defaults to `false`.
     */
    isSilenced?: boolean;
    /**
     * The types of events that will trigger notifications for this subscription.
     */
    triggers: IHardwareHealthNotificationTriggerRequestChild[];
}>;

export function editHardwareHealthNotificationSubscription({
    id,
    isSilenced = false,
    triggers,
}: EditHardwareHealthNotificationSubscription) {
    return sdClient.updateNotificationSubscription({
        subscriptionId: id,
        body: new HardwareHealthNotificationSubscriptionUpdateRequest({
            IsSilenced: isSilenced,
            NotificationTriggers: triggers.map(
                (trigger) => new HardwareHealthNotificationTriggerRequestChild(trigger),
            ),
        }),
    }) as Promise<HardwareHealthNotificationSubscriptionResponse>;
}

/** Type of data returned by mutation function */
type Data = HardwareHealthNotificationSubscriptionResponse;

/** The mutation options object type */
type Options = UseMutationOptions<
    Data,
    unknown,
    EditHardwareHealthNotificationSubscription
>;

export function useEditHardwareHealthNotificationSubscription(options: Options) {
    return useMutation<Data, unknown, EditHardwareHealthNotificationSubscription>({
        meta: { name: 'edit-hardware-health-notification-subscription' },
        mutationFn: (args) => editHardwareHealthNotificationSubscription(args),
        ...options,
    });
}
